import { useNavigate } from "react-router-dom";
import { Dispatch, SetStateAction, useState, useMemo } from "react";

import { IAssessmentReport, IJob, IResume } from "src/models";
import { generateAssessmentReport } from "src/services/job";
import { Button } from "src/shadcn/ui/button";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "src/shadcn/ui/table";
import { Loading } from "src/components/common";
import Pie from "./Pie";

interface IReport {
  report: IAssessmentReport;
  summary: string;
  resume: IResume;
}

interface Props {
  currentJob: IJob;
  currentReport: IReport | null;
  resumeId: string | null;
  setCurrentReport: Dispatch<SetStateAction<IReport | null>>;
}

const Assessment: React.FC<Props> = ({
  currentJob,
  currentReport,
  resumeId,
  setCurrentReport,
}) => {
  const navigate = useNavigate();

  // state
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // functions
  const handleGenerate = async () => {
    if (!resumeId) {
      return;
    }

    setIsLoading(true);
    const res = await generateAssessmentReport(currentJob?.id, resumeId);

    if (res) {
      setCurrentReport({ ...res });
    }
    setIsLoading(false);
  };

  const handleNext = () => {
    navigate(`/job/${currentJob?.id}?step=4`);
  };

  const handleBack = () => {
    navigate(`/job/${currentJob?.id}?step=2`);
  };

  // memos
  const percentageColor = useMemo(() => {
    const per = currentReport?.report?.score ?? 0;

    if (per < 50) {
      return "red";
    }

    if (per >= 50 && per < 70) {
      return "orange";
    }

    return "green";
  }, [currentReport]);

  console.log(currentReport);

  return (
    <div className="w-full grow relative pb-14">
      {isLoading ? (
        <div className="w-full h-[40vh] flex items-center justify-center">
          <Loading />
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between">
            <p className="text-lg font-bold shrink-0">
              Evaluate Your Fit and Gaps
            </p>
          </div>

          <div className="flex items-start gap-2 my-2">
            <div className="shrink-0">
              {currentReport?.report && (
                <Pie
                  percentage={currentReport?.report?.score ?? 0}
                  colour={percentageColor}
                />
              )}
            </div>

            <p className="text-sm text-justify">{currentReport?.summary}</p>
          </div>

          {(currentReport?.report?.desire_to_have &&
            currentReport.report.desire_to_have.length > 0) ||
          (currentReport?.report?.must_have &&
            currentReport.report.must_have.length > 0) ? (
            <div className="border rounded-md overflow-hidden">
              <Table>
                <TableCaption className="hidden">
                  A list of your resumes.
                </TableCaption>
                <TableHeader className="bg-gray-300">
                  <TableRow className="border-b">
                    <TableHead className="font-bold text-black">
                      Requirements
                    </TableHead>
                    <TableHead className="font-bold text-black">Type</TableHead>
                    <TableHead className="font-bold text-black">
                      Match
                    </TableHead>
                    <TableHead className="font-bold text-black">
                      Improvement suggestions
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {currentReport?.report?.must_have &&
                    currentReport?.report?.must_have?.length > 0 &&
                    currentReport.report.must_have.map((report, index) => (
                      <TableRow
                        key={`must-have-${index}`}
                        className="border-b"
                      >
                        <TableCell className="text-sm">
                          {report.criteria}
                        </TableCell>
                        <TableCell className="text-sm">Must have</TableCell>
                        <TableCell className="text-sm">
                          {report.satisfied}
                        </TableCell>
                        <TableCell className="text-sm">
                          {report.suggestion}
                        </TableCell>
                      </TableRow>
                    ))}

                  {currentReport?.report?.desire_to_have &&
                    currentReport?.report?.desire_to_have?.length > 0 &&
                    currentReport.report.desire_to_have.map((report, index) => (
                      <TableRow
                        key={`must-have-${index}`}
                        className="border-b"
                      >
                        <TableCell className="text-sm">
                          {report.criteria}
                        </TableCell>
                        <TableCell className="text-sm">Desirable</TableCell>
                        <TableCell className="text-sm">
                          {report.satisfied}
                        </TableCell>
                        <TableCell className="text-sm">
                          {report.suggestion}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="w-full h-[40vh] flex items-center justify-normal">
              <p className="text-sm w-full text-center">
                Click "Get Report" to view fit-gap assessment
              </p>
            </div>
          )}
        </>
      )}

      <div className="flex justify-end gap-4 absolute bottom-0 right-0">
        <Button
          variant="outline"
          onClick={handleBack}
        >
          Back
        </Button>
        <Button
          disabled={isLoading}
          onClick={handleGenerate}
        >
          Get Report
        </Button>
        <Button
          disabled={isLoading}
          onClick={handleNext}
        >
          Next Step
        </Button>
      </div>
    </div>
  );
};

export default Assessment;
